var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import * as React from "react";
import { useRef } from "react";
import { IconButton } from "@mui/material";
import { scaleLinear } from "d3";
import { clamp } from "lodash";
import AddCircleOutlineSharpIcon from "@mui/icons-material/AddCircleOutlineSharp";
import RemoveCircleOutlineSharpIcon from "@mui/icons-material/RemoveCircleOutlineSharp";
import { SketchPicker } from "react-color";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { styled } from "@mui/system";
import { getColorArrayFromBreakPoints } from "../Utils/legendCommonFunction";
import { ColorScale } from "../BreakPoint/ColorScale";
var RAIL_HEIGHT = 16;
var pointer_width = 12;
var StyledControllersContainer = styled("div")({
    display: "flex",
    flexDirection: "row",
    marginTop: "18px",
    marginLeft: "30px",
});
var StyledRail = styled("div")({
    position: "absolute",
    width: "100%",
    height: "100%",
});
var StyledPointerContainer = styled("div")({
    userSelect: "none",
    cursor: "pointer",
});
var StyledRoot = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        marginTop: "17px",
        marginLeft: "37px",
        height: "140px",
        width: "240px",
    });
});
var StyledColorScaleContainer = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        height: RAIL_HEIGHT,
        borderRadius: theme.shape.borderRadius,
        overflow: "visible",
        position: "relative",
        marginTop: "9px",
    });
});
var StyledTextureContainer = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        position: "absolute",
        width: "100%",
        height: "100%",
        borderRadius: theme.shape.borderRadius,
        cursor: "pointer",
        overflow: "hidden",
        zIndex: 999,
    });
});
var StyledPointer = styled("div")(function (_a) {
    var theme = _a.theme;
    return ({
        position: "absolute",
        top: 20,
        width: pointer_width,
        height: RAIL_HEIGHT,
        borderWidth: "thin",
        borderStyle: "solid",
        borderColor: "white",
        borderTopLeftRadius: theme.shape.borderRadius,
        borderTopRightRadius: theme.shape.borderRadius,
        boxSizing: "border-box",
    });
});
var StyledPointerArrow = styled("div")({
    position: "absolute",
    top: 16,
    width: 0,
    height: 0,
    borderLeft: "".concat(pointer_width / 2, "px solid transparent"),
    borderRight: "".concat(pointer_width / 2, "px solid transparent"),
    borderTop: "4px solid black",
    transform: "rotate(180deg)",
});
var StyledSketchPicker = styled("div")({
    marginTop: "120px",
    position: "fixed",
});
export var BreakPointComp = function (_a) {
    var colorScaleBreakpoints = _a.colorScaleBreakpoints, setColorScaleBreakpoints = _a.setColorScaleBreakpoints, editedBreakpoint = _a.editedBreakpoint, customScalesName = _a.customScalesName;
    var divRef = useRef(null);
    var pointer_width = 12;
    var _b = React.useState({
        width: 0,
        left: 0,
    }), rectBox = _b[0], setRectBox = _b[1];
    var setBoundingClientRect = React.useCallback(function (node) {
        node && setRectBox(node.getBoundingClientRect());
    }, []);
    var onMouseUp = React.useCallback(function () {
        isBreakpointMovingRef.current = false;
    }, []);
    var _c = React.useState(0), getIndex = _c[0], setIndex = _c[1];
    var onMouseMove = React.useCallback(function (e) {
        var _a, _b, _c;
        if (!isBreakpointMovingRef.current) {
            return;
        }
        var offset = (_c = (_b = (_a = e.touches) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.clientX) !== null && _c !== void 0 ? _c : e.clientX;
        var clampedNewWidth = clamp(offset, rectBox.left, rectBox.right) - rectBox.left;
        var normalizer = scaleLinear().domain([0, rectBox.width]);
        var normalizedPosition = normalizer(clampedNewWidth);
        var firstItemIndex = 0;
        var lastItemIndex = colorScaleBreakpoints.length - 1;
        if (getIndex !== firstItemIndex && getIndex !== lastItemIndex) {
            setColorScaleBreakpoints(function (items) {
                return items.map(function (item, index) {
                    return index === selectedIndexRef.current
                        ? __assign(__assign({}, item), { position: normalizedPosition }) : item;
                });
            });
        }
    }, [
        rectBox.left,
        rectBox.right,
        rectBox.width,
        colorScaleBreakpoints.length,
        getIndex,
        setColorScaleBreakpoints,
    ]);
    var orderedSelectedColors = React.useMemo(function () {
        return Object.values(colorScaleBreakpoints).sort(function (a, b) { return a.position - b.position; });
    }, [colorScaleBreakpoints]);
    var arrayOfColors = React.useMemo(function () { return getColorArrayFromBreakPoints(orderedSelectedColors); }, [orderedSelectedColors]);
    React.useEffect(function () {
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
        if (editedBreakpoint) {
            editedBreakpoint(colorScaleBreakpoints);
        }
        return function () {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };
    }, [
        onMouseMove,
        colorScaleBreakpoints,
        colorScaleBreakpoints.length,
        onMouseUp,
        editedBreakpoint,
    ]);
    var isBreakpointMovingRef = React.useRef(false);
    var selectedIndexRef = React.useRef(0);
    var _d = React.useState(1), selectedIndex = _d[0], setSelectedIndex = _d[1];
    var onMouseDown = React.useCallback(function (index) {
        isBreakpointMovingRef.current = true;
        selectedIndexRef.current = index;
        setIndex(index);
        setSelectedIndex(index);
    }, []);
    var selectedPointerArrowStyle = {
        borderTopColor: "black",
    };
    var selectedPointerStyle = {
        borderColor: "black",
        borderWidth: 2,
    };
    var width = 200;
    var onChangeComplete = React.useCallback(function (color) {
        setColorScaleBreakpoints(function (items) {
            return items.map(function (item, index) {
                return index === selectedIndexRef.current
                    ? __assign(__assign({}, item), { color: color.hex }) : item;
            });
        });
    }, [setColorScaleBreakpoints]);
    var _e = React.useState(false), popUpState = _e[0], setPopUpState = _e[1];
    var launchPicker = React.useCallback(function () {
        setPopUpState(true);
    }, []);
    var addBreakpoint = React.useCallback(function () {
        setColorScaleBreakpoints(function (items) { return __spreadArray(__spreadArray([], items.splice(colorScaleBreakpoints.length - 1, 0, {
            position: 0.5,
            color: "#FF69B4",
        }), true), items, true); });
        setSelectedIndex(colorScaleBreakpoints.length);
        selectedIndexRef.current = colorScaleBreakpoints.length;
    }, [colorScaleBreakpoints, setColorScaleBreakpoints]);
    var deleteBreakPoint = React.useCallback(function (indexToDelete) {
        if (indexToDelete !== 0 &&
            indexToDelete !== colorScaleBreakpoints.length - 1) {
            setColorScaleBreakpoints(function (items) {
                return items.filter(function (_, index) { return index !== indexToDelete; });
            });
            if (selectedIndexRef.current === indexToDelete) {
                setSelectedIndex(1);
                selectedIndexRef.current = 1;
            }
        }
    }, [setColorScaleBreakpoints, colorScaleBreakpoints]);
    return (React.createElement(StyledRoot, null,
        React.createElement("label", { style: { fontWeight: "bold" } },
            "Edit : ",
            customScalesName + " Copy"),
        React.createElement(StyledColorScaleContainer, { style: { width: width } },
            React.createElement(StyledTextureContainer, null,
                React.createElement(ColorScale, { arrayOfColors: arrayOfColors })),
            React.createElement(StyledRail, { ref: setBoundingClientRect },
                colorScaleBreakpoints.map(function (_a, index) {
                    var color = _a.color, position = _a.position;
                    var left = position * rectBox.width - pointer_width / 2;
                    var onMoveStart = function () { return onMouseDown(index); };
                    var pointerArrowStyle = selectedIndex === index ? selectedPointerArrowStyle : {};
                    var pointerStyle = selectedIndex === index ? selectedPointerStyle : {};
                    return (React.createElement(StyledPointerContainer, { key: index, onMouseDown: onMoveStart },
                        React.createElement(StyledPointerArrow, { sx: pointerArrowStyle, style: { left: left } }),
                        React.createElement(StyledPointer, { sx: pointerStyle, style: { left: left, backgroundColor: color } })));
                }),
                React.createElement(StyledSketchPicker, { ref: divRef }, popUpState ? (React.createElement(SketchPicker, { color: "red", onChangeComplete: onChangeComplete })) : null))),
        React.createElement(StyledControllersContainer, null,
            React.createElement(IconButton, { size: "medium", color: "primary", onClick: addBreakpoint },
                React.createElement(AddCircleOutlineSharpIcon, { fontSize: "medium" })),
            React.createElement(IconButton, { size: "medium", color: "secondary", onClick: function () { return deleteBreakPoint(selectedIndexRef.current); }, disabled: colorScaleBreakpoints.length === 1 },
                React.createElement(RemoveCircleOutlineSharpIcon, { fontSize: "medium" })),
            React.createElement(IconButton, { color: "primary", size: "medium", onClick: launchPicker },
                React.createElement(ColorizeIcon, { fontSize: "medium" })))));
};
//# sourceMappingURL=BreakPointModule.js.map